import { AxiosResponse } from "axios";

import { axiosInstance, MessageResponse } from "./common";

export interface BusinessDetails {
  cin?: string;
  gstin?: string;
  lei?: string;
  line_of_activity?: string;
}

export interface BusinessKeyPersonnelDetails {
  din?: string;
}

export enum ContactType {
  ADDRESS = "ADDRESS",
  EMAIL = "EMAIL",
  PHONE = "PHONE",
}

export interface StakeholderContactDetail {
  contact_id: string;
  contact_type: ContactType;
  contact_value: string;
  tag?: string;
}

export enum BorrowerConstitution {
  INDIVIDUAL = "INDIVIDUAL",
  SOLE_PROPRIETORSHIP = "SOLE_PROPRIETORSHIP",
  ONE_PERSON_COMPANY = "ONE_PERSON_COMPANY",
  PARTNERSHIP = "PARTNERSHIP",
  LIMITED_LIABILITY_PARTNERSHIP = "LIMITED_LIABILITY_PARTNERSHIP",
  PRIVATE_LIMITED_COMPANY = "PRIVATE_LIMITED_COMPANY",
  PUBLIC_LIMITED_COMPANY = "PUBLIC_LIMITED_COMPANY",
  TRUST = "TRUST",
  SOCIETY = "SOCIETY",
  COOPERATIVE_SOCIETY = "COOPERATIVE_SOCIETY",
  ASSOCIATION_OF_PERSONS = "ASSOCIATION_OF_PERSONS",
}

export const BORROWER_CONSTITUTION_LABELS: Record< BorrowerConstitution, string> = {
  [BorrowerConstitution.INDIVIDUAL]: "Individual",
  [BorrowerConstitution.SOLE_PROPRIETORSHIP]: "Sole Proprietorship",
  [BorrowerConstitution.ONE_PERSON_COMPANY]: "One Person Company",
  [BorrowerConstitution.PARTNERSHIP]: "Partnership",
  [BorrowerConstitution.LIMITED_LIABILITY_PARTNERSHIP]: "Limited Liability Partnership",
  [BorrowerConstitution.PRIVATE_LIMITED_COMPANY]: "Private Limited Company",
  [BorrowerConstitution.PUBLIC_LIMITED_COMPANY]: "Public Limited Company",
  [BorrowerConstitution.TRUST]: "Trust",
  [BorrowerConstitution.SOCIETY]: "Society",
  [BorrowerConstitution.COOPERATIVE_SOCIETY]: "Cooperative Society",
  [BorrowerConstitution.ASSOCIATION_OF_PERSONS]: "Association of Persons",
};

export interface CustomerDetails {
  cif_number?: string;
  constitution?: BorrowerConstitution;
}

export interface EmployeeDetails {
  employee_id?: string;
  designation?: string;
}

export enum KycType {
  PAN = "PAN",
  AADHAAR = "AADHAAR",
  UDYAM_CERTIFICATE = "UDYAM_CERTIFICATE",
  DRIVING_LICENSE = "DRIVING_LICENSE",
  PASSPORT = "PASSPORT",
  VOTER_ID = "VOTER_ID",
}

export interface StakeholderKycDetail {
  kyc_id: string;
  kyc_type: KycType;
  kyc_value: string;
}

export interface UpsertStakeholderKycDetailsRequest {
  kyc_details: StakeholderKycDetail[];
}

export const StakeholderDetailsAPI = {
  updateCustomerDetails: async (
    case_id: string,
    stakeholder_id: string,
    request: CustomerDetails,
  ): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(
        `/v1/cases/${case_id}/stakeholders/${stakeholder_id}/customer-details`,
        request,
      );

    return response.data;
  },

  updateKycDetails: async (
    case_id: string,
    stakeholder_id: string,
    request: UpsertStakeholderKycDetailsRequest,
  ): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(
        `/v1/cases/${case_id}/stakeholders/${stakeholder_id}/kyc-details`,
        request,
      );

    return response.data;
  },

  updateContactDetails: async (
    case_id: string,
    stakeholder_id: string,
    request: StakeholderContactDetail,
  ): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(
        `/v1/cases/${case_id}/stakeholders/${stakeholder_id}/contact-details`,
        request,
      );

    return response.data;
  },

  updateBusinessKeyPersonnelDetails: async (
    case_id: string,
    stakeholder_id: string,
    request: BusinessKeyPersonnelDetails,
  ): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(
        `/v1/cases/${case_id}/stakeholders/${stakeholder_id}/business-key-personnel-details`,
        request,
      );

    return response.data;
  },

  updateBusinessDetails: async (
    case_id: string,
    stakeholder_id: string,
    request: BusinessDetails,
  ): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(
        `/v1/cases/${case_id}/stakeholders/${stakeholder_id}/business-details`,
        request,
      );

    return response.data;
  },

  updateEmployeeDetails: async (
    case_id: string,
    stakeholder_id: string,
    request: EmployeeDetails,
  ): Promise<MessageResponse> => {
    const response: AxiosResponse<MessageResponse> =
      await axiosInstance.put(
        `/v1/cases/${case_id}/stakeholders/${stakeholder_id}/employee-details`,
        request,
      );

    return response.data;
  },
};